/**
 * Đăng nhập Google, lấy về ID token để máy chủ kiểm tra.
 *
 * Dùng Firebase Auth chứ không tự dựng OAuth: dự án đã có sẵn project Firebase
 * (realtime database cho màn live, FCM cho thông báo đẩy) và máy chủ đã nạp
 * FIREBASE_SERVICE_ACCOUNT của đúng project đó - nên chỗ này chỉ cần mở cửa sổ
 * đăng nhập, còn máy chủ chỉ cần verifyIdToken.
 *
 * Ở đây KHÔNG tự suy ra gì về người dùng: email và tên đọc được chỉ để hiển thị
 * trước cho họ thấy; thứ duy nhất có giá trị là `idToken`, vì chỉ nó mới mang
 * chữ ký của Google để máy chủ kiểm tra lại.
 */
import {
  GoogleAuthProvider,
  getAuth,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  UserCredential,
} from "firebase/auth";

import { firebaseApp } from "./firebase";

export interface HoSoGoogle {
  idToken: string;
  email: string;
  name: string | null;
  picture: string | null;
}

export type KetQuaGoogle =
  /** Đăng nhập xong, có token để gửi lên máy chủ. */
  | { trangThai: "ok"; hoSo: HoSoGoogle }
  /** Người dùng tự đóng cửa sổ Google - không phải lỗi, không báo gì. */
  | { trangThai: "huy" }
  /**
   * Trình duyệt chặn cửa sổ bật lên (hay gặp trên điện thoại), nên đã chuyển
   * sang cách rời trang: trang hiện tại sắp bị thay, không làm gì thêm.
   */
  | { trangThai: "dangChuyenHuong" };

/** Mã lỗi Firebase nghĩa là "người dùng đổi ý", không phải sự cố. */
const NGUOI_DUNG_HUY = [
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
];

/**
 * Câu tiếng Việt cho lỗi của Firebase, kèm việc phải làm.
 *
 * Vì sao không gộp hết thành một câu "không mở được, thử lại": ba mã dưới đây
 * KHÔNG phải trục trặc nhất thời mà là cấu hình thiếu bên Firebase Console -
 * người dùng bấm lại bao nhiêu lần cũng vẫn hỏng. Nói đúng tên chỗ cần sửa thì
 * mất vài phút; nói "thử lại" thì mất cả buổi mò.
 */
export const thongDiepLoiGoogle = (e: any): string => {
  // Luôn ghi nguyên lỗi ra console: mã lỗi của Firebase là thứ duy nhất lần ra
  // được nguyên nhân, và nó không chứa gì bí mật.
  console.error("Đăng nhập Google thất bại:", e?.code, e);

  const mien = typeof window !== "undefined" ? window.location.hostname : "";
  switch (e?.code) {
    case "auth/unauthorized-domain":
      return `Tên miền ${mien} chưa được Firebase cho phép đăng nhập. Thêm nó vào Firebase Console > Authentication > Settings > Authorized domains.`;
    case "auth/operation-not-allowed":
      return "Đăng nhập bằng Google chưa được bật trong Firebase Console > Authentication > Sign-in method.";
    case "auth/configuration-not-found":
      return "Firebase Authentication chưa được bật cho dự án này (Firebase Console > Authentication > Get started).";
    case "auth/network-request-failed":
      return "Không kết nối được tới Google. Kiểm tra mạng rồi thử lại.";
    default:
      // Còn lại thì ít nhất cũng đưa mã ra, đừng bắt người đọc đoán.
      return `Không mở được đăng nhập Google${e?.code ? ` (${e.code})` : ""}, vui lòng thử lại hoặc dùng số điện thoại.`;
  }
};

/** Mã lỗi nghĩa là "cửa sổ bật lên không dùng được ở môi trường này". */
const POPUP_KHONG_DUNG_DUOC = [
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
  "auth/web-storage-unsupported",
];

const layAuth = () => getAuth(firebaseApp);

const taoProvider = () => {
  const provider = new GoogleAuthProvider();
  // Luôn hỏi chọn tài khoản: nhiều người có cả gmail cá nhân và gmail công
  // việc, mặc định của Google là vào thẳng cái đang đăng nhập dở trên máy.
  provider.setCustomParameters({ prompt: "select_account" });

  return provider;
};

const docHoSo = async (ketQua: UserCredential): Promise<HoSoGoogle> => {
  const idToken = await ketQua.user.getIdToken();
  const hoSo: HoSoGoogle = {
    idToken,
    email: ketQua.user.email ?? "",
    name: ketQua.user.displayName ?? null,
    picture: ketQua.user.photoURL ?? null,
  };

  /*
   * Đăng xuất khỏi Firebase ngay sau khi có token.
   *
   * Phiên đăng nhập của hệ thống là JWT của máy chủ Biadi, không phải phiên
   * Firebase. Giữ lại phiên Firebase chỉ tạo ra hai thứ trạng thái đăng nhập
   * trên cùng một máy, và lần sau bấm "đăng nhập bằng Google" sẽ vào thẳng
   * tài khoản cũ mà không hỏi lại - đúng cái mà máy dùng chung không nên làm.
   */
  await signOut(layAuth()).catch(() => undefined);

  return hoSo;
};

const phanLoaiLoi = (e: any): KetQuaGoogle | null => {
  const ma = e?.code;
  if (NGUOI_DUNG_HUY.includes(ma)) {
    return { trangThai: "huy" };
  }

  return null;
};

/**
 * Mở cửa sổ đăng nhập Google.
 *
 * Bị chặn cửa sổ bật lên thì tự chuyển sang cách rời trang (hay gặp trên
 * Safari/iOS và trong webview) - lúc đó trang sẽ được tải lại và kết quả đọc
 * bằng ketQuaChuyenHuong().
 */
export const dangNhapGoogle = async (): Promise<KetQuaGoogle> => {
  const auth = layAuth();
  try {
    return { trangThai: "ok", hoSo: await docHoSo(await signInWithPopup(auth, taoProvider())) };
  } catch (e: any) {
    const daBiet = phanLoaiLoi(e);
    if (daBiet) {
      return daBiet;
    }

    if (POPUP_KHONG_DUNG_DUOC.includes(e?.code)) {
      await signInWithRedirect(auth, taoProvider());

      return { trangThai: "dangChuyenHuong" };
    }

    throw e;
  }
};

/**
 * Đọc kết quả của lần đăng nhập theo cách rời trang.
 *
 * Gọi khi màn đăng nhập được mở: không có gì đang chờ thì trả về 'huy' (nghĩa
 * là "không có gì để làm"), chứ không phải lỗi.
 */
export const ketQuaChuyenHuong = async (): Promise<KetQuaGoogle> => {
  try {
    const ketQua = await getRedirectResult(layAuth());
    if (!ketQua) {
      return { trangThai: "huy" };
    }

    return { trangThai: "ok", hoSo: await docHoSo(ketQua) };
  } catch (e: any) {
    return phanLoaiLoi(e) ?? { trangThai: "huy" };
  }
};
